import React, {useCallback, useMemo} from 'react';
import {classes, style} from './wix-video-resource-player.st.css';
import {
    WixImageInfo,
    wixMediaManagerMediaGetFileInfo,
    wixMediaManagerVideoFileInfoGetHlsUrl,
    WixVideoInfoCustomQualitySelector,
    wixVideoInfoGetHlsUrl,
    wixVideoInfoGetVideoUrl,
    WixVideoInfoQualitySelector,
    WixVideoResource
} from "@wix/devzai-common-wix";
import {useHashedMemo, useRenderState, VideoPlayer, VideoPlayerMediaStreamTypes} from "@wix/devzai-utils-react";
import {
    arrayRemoveNullishValues,
    asyncProcessPromisable,
    asyncWithRetries,
    Evaluable,
    evaluateFunction,
    evaluateWhenFunction,
    IIsomorphicHttpClient,
    OmitStrict,
    Values
} from "@wix/devzai-utils-common";

export const WixVideoResourcePlayerPreferredFormat = {
    Static: 'Static',
    Adaptive: 'Adaptive'
} as const;

export type WixVideoResourcePlayerPreferredFormat = Values<typeof WixVideoResourcePlayerPreferredFormat>;

export const WixVideoResourcePlayer = React.memo(function WixVideoResourcePlayer (props: WixVideoResourcePlayer.Props) {
    const {
        wixVideoResource,
        httpClient,
        staticVideoQuality = WixVideoInfoQualitySelector.Highest,
        preferredVideoFormat = WixVideoResourcePlayerPreferredFormat.Adaptive,
        poster,
        videoPlayerRef,

        className,
        ...htmlAttributes
    } = props;

    const memoizedWixVideoResource = useHashedMemo(wixVideoResource);

    const renderState = useRenderState({
        httpClient: httpClient
    });

    const src = useMemo(() => {

        const httpClient = renderState.current.httpClient;

        return asyncProcessPromisable(evaluateFunction(() => {

            switch (preferredVideoFormat) {
                case WixVideoResourcePlayerPreferredFormat.Static: {
                    return undefined;
                }
                case WixVideoResourcePlayerPreferredFormat.Adaptive: {
                    const hlsUrl = wixVideoInfoGetHlsUrl(memoizedWixVideoResource);

                    if (hlsUrl) {
                        return hlsUrl;
                    } else {
                        return evaluateFunction(async () => {

                            return wixMediaManagerVideoFileInfoGetHlsUrl(await asyncWithRetries(
                                async () => wixMediaManagerMediaGetFileInfo(httpClient, memoizedWixVideoResource),
                                {
                                    retries: 1,
                                    interval: 500,
                                }
                            ))
                        })
                    }
                }
            }


        }), hlsFileUrl => {
            return arrayRemoveNullishValues([
                {url: wixVideoInfoGetVideoUrl(memoizedWixVideoResource)},
                hlsFileUrl ? {url: hlsFileUrl, type: VideoPlayerMediaStreamTypes.HLS} : undefined
            ])
        })

    }, [memoizedWixVideoResource, preferredVideoFormat, renderState])

    const overlayRenderCallback = useCallback((renderProps: VideoPlayer.OverlayRenderProps) => {
        return poster ? evaluateWhenFunction(poster, {
            ...renderProps,
            poster: wixVideoResource.poster ?? null
        }) : null;
    }, [wixVideoResource, poster]);

    return (
        <VideoPlayer
            {...htmlAttributes}
            ref={videoPlayerRef}
            className={style(classes.root, className)}
            src={src}
            poster={overlayRenderCallback}
        />
    );
});

export namespace WixVideoResourcePlayer {

    export type OverlayRenderProps = VideoPlayer.OverlayRenderProps & {
        poster: WixImageInfo | null;
    }

    export interface Props extends OmitStrict<VideoPlayer.Props, 'src'> {
        videoPlayerRef?: React.Ref<VideoPlayer>;
        wixVideoResource: WixVideoResource;
        httpClient: IIsomorphicHttpClient;
        preferredVideoFormat?: WixVideoResourcePlayerPreferredFormat;
        staticVideoQuality?: WixVideoInfoQualitySelector | WixVideoInfoCustomQualitySelector;
        poster?: Evaluable<(renderProps: OverlayRenderProps) => React.ReactNode>;
    }
}